<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import BlocksEmailImageWithLink from '../../block-editor/blocks/edit/email/imageWithLink.vue';

const props = defineProps({
    mode: { type: String, required: true },
    specialist: { type: String, required: true },
    exitUrl: { type: String, required: true },
    generateUrl: { type: String, required: true },
    pollUrlPrefix: { type: String, required: true },
    conversationUrl: { type: String, required: true },
    newConversationUrl: { type: String, required: true },
    saveUrl: { type: String, required: true },
    sendTestUrl: { type: String, required: true },
    tagsUrl: { type: String, required: true },
    recipientCountUrl: { type: String, required: true },
    automationId: { type: [String, Number], default: null },
    conversationId: { type: [String, Number], default: null },
    initialMessage: { type: String, default: '' },
});

const messages = ref([]);
const context = ref({});
const currentConversationId = ref(props.conversationId);
const isGenerating = ref(false);
const pollTimer = ref(null);
const isSaving = ref(false);
const saveStatus = ref('')
const isSendingTest = ref(false);
const draftAutomationId = ref(null);
const isDirty = ref(false);
const rewriteUndoState = ref(null);
const lastRewriteWasUndone = ref(false);
const imageEditorVisible = ref(false);
const editingImageBlockIndex = ref(null);
const exitAutomationId = ref(null);
const AUTOMATION_IMMEDIATE_SCHEDULES = ['now', 'send_immediately'];

// Email data
const emailData = ref(null);
const editableSubject = ref('');
const editablePreviewText = ref('');
const editableBlocks = ref([]);

// Settings
const settings = reactive({
    name: '',
    targetTags: [],
    excludeTags: [],
    schedule: 'draft',
    triggerTag: null,
    delayHours: 2,
});

const availableTags = ref([]);

const getScopedAutomationId = () => draftAutomationId.value || props.automationId || null;
const hasScopedAutomationId = computed(() => getScopedAutomationId() !== null);
const getAutomationIdFromUrl = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.location.search ? new URLSearchParams(window.location.search).get('automation_id') : null;
};
const resolvedExitUrl = computed(() => {
    if (!exitAutomationId.value) {
        return props.exitUrl;
    }

    return route(
        props.mode === 'newsletter' ? 'bo.crm.newsletters.edit' : 'bo.crm.automations.edit',
        exitAutomationId.value
    );
});
const syncAutomationIdUrl = (automationId = null) => {
    exitAutomationId.value = automationId ? String(automationId) : null;

    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);

    if (automationId) {
        url.searchParams.set('automation_id', automationId);
    } else {
        url.searchParams.delete('automation_id');
    }

    window.history.replaceState({}, '', url.toString());
};

const buildFallbackDraftName = (subject = '') => {
    const normalizedSubject = String(subject)
        .replace(/\[[^\]]+\]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    return normalizedSubject.slice(0, 255);
};

const normalizeImageBlockContent = (content = {}) => ({
    ...content,
    mediaBankEntryId: content.mediaBankEntryId ?? content.media_bank_entry_id ?? null,
    imagelink: content.imagelink ?? null,
});

const normalizeEmailBlocks = (blocks = []) => blocks.map((block) => {
    if (!block) {
        return block;
    }

    if (block.type === 'email-image' || block.type === 'email-image-with-link') {
        return {
            ...block,
            type: 'email-image-with-link',
            content: normalizeImageBlockContent(block.content || {}),
        };
    }

    return {
        ...block,
        content: block.content || {},
    };
});

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const hasEditableEmail = computed(() => editableBlocks.value.length > 0);
const canUndoRewrite = computed(() => rewriteUndoState.value !== null);
const editorTitle = computed(() => settings.name || _mft('email-ai:settings.namePlaceholder'));
const isAutomationScheduleReady = (schedule) => {
    if (AUTOMATION_IMMEDIATE_SCHEDULES.includes(schedule)) {
        return true;
    }

    return !!schedule && !['draft', 'schedule'].includes(schedule);
};

const canSave = computed(() => {
    if (!hasEditableEmail.value || !settings.name) return false;

    if (props.mode === 'newsletter' && settings.schedule === 'draft') {
        return true;
    }

    if (props.mode === 'newsletter' && settings.schedule !== 'draft') {
        return settings.targetTags.length > 0;
    }

    if (settings.schedule === 'draft') {
        return true;
    }

    if (!isAutomationScheduleReady(settings.schedule)) {
        return false;
    }

    return !!settings.triggerTag;
});

const saveButtonLabel = computed(() => {
    if (props.mode === 'newsletter') {
        if (settings.schedule === 'draft') return _mft('email-ai:settings.saveWithoutSending');
        if (settings.schedule !== 'now') return _mft('email-ai:settings.saveAndSchedule');

        return _mft('email-ai:settings.saveAndSend');
    }

    if (settings.schedule === 'draft') {
        return _mft('communication:automation.save');
    }

    if (!AUTOMATION_IMMEDIATE_SCHEDULES.includes(settings.schedule)) {
        return _mft('communication:automation.saveAndSchedule');
    }

    return _mft('email-ai:settings.saveAndActivate');
});

const shouldRedirectAfterSave = computed(() => {
    if (props.mode === 'newsletter') {
        return settings.schedule !== 'draft';
    }

    return settings.schedule === 'send_immediately';
});

const markDirty = () => {
    isDirty.value = true;
};

const markCurrentStateAsSaved = () => {
    isDirty.value = false;
};

const clearRewriteUndoState = () => {
    rewriteUndoState.value = null;
};

const closeImageEditor = () => {
    imageEditorVisible.value = false;
    editingImageBlockIndex.value = null;
};

const handleExit = (event) => {
    if (!isDirty.value) {
        return;
    }

    if (!window.confirm(_mft('pagebuilder:warning.unsavedChanges'))) {
        event.preventDefault();
    }
};

const resetEditorState = () => {
    messages.value = [];
    context.value = {};
    emailData.value = null;
    editableSubject.value = '';
    editablePreviewText.value = '';
    editableBlocks.value = [];
    clearRewriteUndoState();
    lastRewriteWasUndone.value = false;
    closeImageEditor();
    draftAutomationId.value = props.automationId || null;
    Object.assign(settings, {
        name: '',
        targetTags: [],
        excludeTags: [],
        schedule: 'draft',
        triggerTag: null,
        delayHours: 2,
    });
    isDirty.value = false;
};

const applyEditableEmailData = (data, { markAsDirty = false } = {}) => {
    const normalizedBlocks = normalizeEmailBlocks(data?.blocks || []);

    emailData.value = data ? {
        ...data,
        blocks: normalizedBlocks,
    } : null;
    editableSubject.value = data?.subject || '';
    editablePreviewText.value = data?.preview_text || '';
    editableBlocks.value = normalizedBlocks;

    if (markAsDirty) {
        markDirty();
    }
};

const getCurrentEditableEmailData = () => ({
    ...(emailData.value ? deepClone(emailData.value) : {}),
    subject: editableSubject.value,
    preview_text: editablePreviewText.value,
    blocks: normalizeEmailBlocks(deepClone(editableBlocks.value)),
});

const getCurrentSettingsState = () => deepClone({
    name: settings.name,
    targetTags: settings.targetTags,
    excludeTags: settings.excludeTags,
    schedule: settings.schedule,
    triggerTag: settings.triggerTag,
    delayHours: settings.delayHours,
});

const createRewriteUndoState = () => ({
    emailData: getCurrentEditableEmailData(),
    settings: getCurrentSettingsState(),
    isDirty: isDirty.value,
});

const restoreRewriteUndoState = (snapshot) => {
    if (!snapshot) {
        return;
    }

    closeImageEditor();
    applyEditableEmailData(snapshot.emailData);
    Object.assign(settings, snapshot.settings);
    isDirty.value = snapshot.isDirty;
};

const applyGeneratedEmail = (data) => {
    lastRewriteWasUndone.value = false;

    if (emailData.value) {
        rewriteUndoState.value = createRewriteUndoState();
    } else {
        clearRewriteUndoState();
    }

    closeImageEditor();
    applyEditableEmailData(data, { markAsDirty: true });

    if (!settings.name && props.mode === 'newsletter') {
        settings.name = buildFallbackDraftName(data?.subject || '');
    }
};

const getCurrentDraftContextForGenerate = () => {
    if (!hasEditableEmail.value) {
        return null;
    }

    return {
        name: settings.name,
        subject: editableSubject.value,
        preview_text: editablePreviewText.value,
        send_schedule: settings.schedule,
        target_tags: settings.targetTags.map((tag) => tag.name),
        exclude_tags: settings.excludeTags.map((tag) => tag.name),
        trigger_tag_name: settings.triggerTag?.name || null,
        delay_hours: settings.delayHours,
        blocks: normalizeEmailBlocks(deepClone(editableBlocks.value)),
    };
};

const loadEditorData = (data) => {
    resetEditorState();

    currentConversationId.value = data.id;
    messages.value = data.messages || [];
    context.value = data.context || {};
    draftAutomationId.value = data.automation_id || props.automationId || null;

    if (data.prefill_email_data) {
        applyEditableEmailData(data.prefill_email_data);
    } else if (data.context?.latest_email_data) {
        applyEditableEmailData(data.context.latest_email_data);
    }

    if (data.prefill_settings) {
        applySuggestedSettings(data.prefill_settings);
    } else if (data.context?.suggested_settings) {
        applySuggestedSettings(data.context.suggested_settings);
    }

    markCurrentStateAsSaved();
};

const loadConversation = async () => {
    try {
        const url = new URL(props.conversationUrl, window.location.origin);
        const automationId = getScopedAutomationId();

        if (automationId) {
            url.searchParams.set('automation_id', automationId);
        }

        const response = await fetch(url.toString());
        const data = await response.json();
        loadEditorData(data);
    } catch (e) {
        console.error('Failed to load conversation', e);
    }
};

const loadTags = async () => {
    try {
        const response = await fetch(props.tagsUrl);
        const data = await response.json();
        availableTags.value = data.tags || [];
    } catch (e) {
        console.error('Failed to load tags', e);
    }
};

const getAvailableTagsByIds = (tagIds = []) => {
    if (!Array.isArray(tagIds) || availableTags.value.length === 0) {
        return [];
    }

    const normalizedIds = new Set(
        tagIds
            .map((tagId) => Number(tagId))
            .filter((tagId) => !Number.isNaN(tagId))
    );

    if (normalizedIds.size === 0) {
        return [];
    }

    return availableTags.value.filter((tag) => normalizedIds.has(Number(tag.id)));
};

const getAvailableTagsByNames = (tagNames = []) => {
    if (!Array.isArray(tagNames) || availableTags.value.length === 0) {
        return [];
    }

    return availableTags.value.filter((tag) => tagNames.includes(tag.name));
};

const getAvailableTriggerTag = (suggested) => {
    const [triggerTag] = getAvailableTagsByIds(
        suggested?.trigger_tag_id !== undefined && suggested?.trigger_tag_id !== null
            ? [suggested.trigger_tag_id]
            : []
    );

    if (triggerTag) {
        return triggerTag;
    }

    if (!suggested?.trigger_tag_name || availableTags.value.length === 0) {
        return null;
    }

    return availableTags.value.find((tag) => tag.name === suggested.trigger_tag_name) || null;
};

const sendMessage = async (text) => {
    if (isGenerating.value) return;
    messages.value.push({ role: 'user', content: text });
    isGenerating.value = true;

    try {
        const response = await fetch(props.generateUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({
                prompt: text,
                conversation_id: currentConversationId.value,
                automation_id: getScopedAutomationId(),
                current_draft: getCurrentDraftContextForGenerate(),
                last_rewrite_was_undone: lastRewriteWasUndone.value,
            }),
        });

        const data = await response.json();
        currentConversationId.value = data.conversation_id;

        if (data.status === 'budget_exceeded') {
            isGenerating.value = false;
            pushFailureMessage(data.status);
            return;
        }

        startPolling();
    } catch (e) {
        console.error('Failed to send message', e);
        isGenerating.value = false;
    }
};

const pushFailureMessage = (status) => {
    messages.value.push({
        role: 'assistant',
        content: status === 'budget_exceeded'
            ? _mft('email-ai:budgetExceeded')
            : _mft('email-ai:errorMessage'),
        budgetExceeded: status === 'budget_exceeded',
    });
};

const startPolling = () => {
    if (pollTimer.value) clearInterval(pollTimer.value);

    pollTimer.value = setInterval(async () => {
        try {
            const response = await fetch(`${props.pollUrlPrefix}${currentConversationId.value}/poll`);
            const data = await response.json();

            if (data.status === 'completed') {
                stopPolling();
                isGenerating.value = false;

                const msg = {
                    role: 'assistant',
                    content: data.message || '',
                    type: data.type,
                };

                if (data.type === 'email' && data.email_data) {
                    msg.email_data = data.email_data;
                    applyGeneratedEmail(data.email_data);
                }

                if (data.suggested_settings) {
                    msg.suggested_settings = data.suggested_settings;
                }

                messages.value.push(msg);
            } else if (data.status === 'failed' || data.status === 'budget_exceeded') {
                stopPolling();
                isGenerating.value = false;
                pushFailureMessage(data.status);
            }
        } catch (e) {
            console.error('Polling failed', e);
        }
    }, 2000);
};

const stopPolling = () => {
    if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
    }
};

const startNewConversation = async () => {
    try {
        const response = await fetch(props.newConversationUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({
                automation_id: getScopedAutomationId(),
            }),
        });

        const data = await response.json();
        loadEditorData(data);
    } catch (e) {
        console.error('Failed to create new conversation', e);
    }
};

const applySuggestedSettings = (suggested) => {
    if (!suggested) return;
    if (suggested.name && !settings.name) settings.name = suggested.name;
    if (suggested.send_schedule) settings.schedule = suggested.send_schedule;
    if (suggested.delay_hours !== undefined) settings.delayHours = suggested.delay_hours;

    const resolvedTargetTags = getAvailableTagsByIds(suggested.target_tag_ids);
    if (resolvedTargetTags.length > 0) {
        settings.targetTags = resolvedTargetTags;
    } else if (suggested.target_tags && availableTags.value.length) {
        settings.targetTags = getAvailableTagsByNames(suggested.target_tags);
    }

    const resolvedExcludeTags = getAvailableTagsByIds(suggested.exclude_tag_ids);
    if (resolvedExcludeTags.length > 0) {
        settings.excludeTags = resolvedExcludeTags;
    } else if (suggested.exclude_tags && availableTags.value.length) {
        settings.excludeTags = getAvailableTagsByNames(suggested.exclude_tags);
    }

    const resolvedTriggerTag = getAvailableTriggerTag(suggested);
    if (resolvedTriggerTag) {
        settings.triggerTag = resolvedTriggerTag;
    }
};

const undoLatestRewrite = () => {
    if (!rewriteUndoState.value) {
        return;
    }

    restoreRewriteUndoState(rewriteUndoState.value);
    clearRewriteUndoState();
    lastRewriteWasUndone.value = true;
};

const acceptSetting = ({ key, value }) => {
    if (key === 'schedule') settings.schedule = value;
    else if (key === 'delay_hours') settings.delayHours = value;
    else if (key === 'target_tags') {
        settings.targetTags = availableTags.value.filter(t => value.includes(t.name));
    } else if (key === 'exclude_tags') {
        settings.excludeTags = availableTags.value.filter(t => value.includes(t.name));
    } else if (key === 'trigger_tag_name') {
        settings.triggerTag = availableTags.value.find(t => t.name === value) || null;
    }
    markDirty();
};

const updateSettings = (newSettings) => {
    Object.assign(settings, newSettings);
    markDirty();
};

const updateSubject = (value) => {
    editableSubject.value = value;
    markDirty();
};

const updatePreviewText = (value) => {
    editablePreviewText.value = value;
    markDirty();
};

const updateBlocks = (value) => {
    editableBlocks.value = normalizeEmailBlocks(value);

    const editingBlock = editingImageBlockIndex.value !== null
        ? editableBlocks.value[editingImageBlockIndex.value]
        : null;

    if (editingImageBlockIndex.value !== null && editingBlock?.type !== 'email-image-with-link') {
        closeImageEditor();
    }

    markDirty();
};

const openImageEditor = (index) => {
    const block = editableBlocks.value[index];

    if (!block || block.type !== 'email-image-with-link') {
        return;
    }

    editingImageBlockIndex.value = index;
    imageEditorVisible.value = true;
};

const updateImageBlockContent = (value) => {
    if (editingImageBlockIndex.value === null) {
        return;
    }

    const updatedBlocks = [...editableBlocks.value];
    const block = updatedBlocks[editingImageBlockIndex.value];

    if (!block) {
        closeImageEditor();
        return;
    }

    updatedBlocks[editingImageBlockIndex.value] = {
        ...block,
        type: 'email-image-with-link',
        content: normalizeImageBlockContent(value),
    };

    updateBlocks(updatedBlocks);
};

const handleSave = async () => {
    if (isSaving.value || !editableBlocks.value.length) return;
    isSaving.value = true;
    saveStatus.value = 'waiting'

    try {
        const payload = {
            name: settings.name,
            subject: editableSubject.value,
            preview_text: editablePreviewText.value,
            blocks: editableBlocks.value,
            conversation_id: currentConversationId.value,
            automation_id: draftAutomationId.value,
            target_tag_ids: settings.targetTags.map(t => t.id),
            exclude_tag_ids: settings.excludeTags.map(t => t.id),
            schedule: settings.schedule,
        };

        if (props.mode === 'automation') {
            payload.trigger_tag_id = settings.triggerTag?.id || null;
            payload.delay_hours = settings.delayHours;
        }

        const response = await fetch(props.saveUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (data.automation_id) {
            draftAutomationId.value = data.automation_id;
        }
        if (data.success && data.redirect_url && shouldRedirectAfterSave.value) {
            clearRewriteUndoState();
            lastRewriteWasUndone.value = false;
            markCurrentStateAsSaved();
            window.location.href = data.redirect_url;
        }
        if (data.success && !shouldRedirectAfterSave.value) {
            saveStatus.value = 'success'
            clearRewriteUndoState();
            lastRewriteWasUndone.value = false;
            markCurrentStateAsSaved();
        }
    } catch (e) {
        saveStatus.value = 'error'
        console.error('Failed to save', e);
    } finally {
        isSaving.value = false;
    }
};

const handleSendTest = async () => {
    if (isSendingTest.value || !editableBlocks.value.length) return;
    isSendingTest.value = true;

    try {
        const response = await fetch(props.sendTestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({
                subject: editableSubject.value,
                preview_text: editablePreviewText.value,
                blocks: editableBlocks.value,
                conversation_id: currentConversationId.value,
                automation_id: getScopedAutomationId(),
            }),
        });

        const data = await response.json();
        if (data.automation_id) {
            draftAutomationId.value = data.automation_id;
        }
    } catch (e) {
        console.error('Failed to send test', e);
    } finally {
        isSendingTest.value = false;
    }
};

watch(draftAutomationId, (automationId) => {
    syncAutomationIdUrl(automationId);
});

onMounted(() => {
    exitAutomationId.value = getAutomationIdFromUrl();
    loadTags().then(() => loadConversation());
});
</script>

<template>
    <div class="flex flex-col h-screen bg-gray-50">
        <!-- Header -->
        <div class="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200 shadow-sm z-10">
            <div class="flex min-w-0 flex-1 items-center gap-3">
                <a :href="resolvedExitUrl" @click="handleExit">
                    <button-secondary>
                        <mf-translate domainkey="shared:action.exit"></mf-translate>
                    </button-secondary>
                </a>
                <span
                    class="min-w-0 max-w-[8rem] truncate text-sm font-medium sm:max-w-sm"
                    :class="settings.name ? 'text-gray-700' : 'text-gray-400'"
                >{{ editorTitle }}</span>
            </div>
            <div class="flex flex-shrink-0 items-center gap-2">
                <button-with-confirmation
                    v-if="!hasScopedAutomationId"
                    @confirmed="startNewConversation"
                    style-type="secondary"
                    :confirm-text="_mft('shared:confirm.changesLost')"
                    :confirm-button-label="_mft('shared:confirm.positive')"
                    :abort-button-label="_mft('shared:confirm.negative')"
                >
                    <mf-translate domainkey="email-ai:newChat"></mf-translate>
                </button-with-confirmation>
                <button-secondary
                    @click="handleSendTest"
                    :disabled="isSendingTest || !hasEditableEmail"
                >
                    <span v-if="isSendingTest">
                        <mf-translate domainkey="email-ai:preview.sending"></mf-translate>
                    </span>
                    <span v-else>
                        <mf-translate domainkey="email-ai:preview.sendTest"></mf-translate>
                    </span>
                </button-secondary>
                <button-success
                    @click="handleSave"
                    :state="saveStatus"
                    :disabled="!canSave"
                >
                    <span>{{ saveButtonLabel }}</span>
                </button-success>
            </div>
        </div>

        <!-- Main content -->
        <div class="flex-1 flex overflow-hidden">
            <!-- Left panel: preview -->
            <div class="flex-1 flex flex-col overflow-hidden bg-gray-50">
                <email-ai-preview
                    :subject="editableSubject"
                    :preview-text="editablePreviewText"
                    :blocks="editableBlocks"
                    :mode="mode"
                    :is-sending-test="isSendingTest"
                    :show-send-test-button="false"
                    @update:subject="updateSubject"
                    @update:preview-text="updatePreviewText"
                    @update:blocks="updateBlocks"
                    @edit-image-block="openImageEditor"
                    @send-test="handleSendTest"
                ></email-ai-preview>
            </div>

            <!-- Right sidebar: chat + settings -->
            <div class="w-[400px] flex-shrink-0 flex flex-col border-l border-gray-200 bg-white">
                <!-- Chat (70%) -->
                <div class="flex-[7] flex flex-col overflow-hidden min-h-0">
                    <email-ai-chat
                        :messages="messages"
                        :is-generating="isGenerating"
                        :mode="mode"
                        :specialist="specialist"
                        :settings="settings"
                        :available-tags="availableTags"
                        :can-undo-rewrite="canUndoRewrite"
                        :initial-message="initialMessage"
                        @send="sendMessage"
                        @accept-setting="acceptSetting"
                        @undo-rewrite="undoLatestRewrite"
                    ></email-ai-chat>
                </div>

                <!-- Settings panel (30%) -->
                <div class="flex-[3] border-t border-gray-200 overflow-y-auto">
                    <email-ai-settings-panel
                        :mode="mode"
                        :settings="settings"
                        :available-tags="availableTags"
                        :has-email="!!editableBlocks.length"
                        :is-saving="isSaving"
                        :recipient-count-url="recipientCountUrl"
                        :save-status="saveStatus"
                        :show-save-button="false"
                        @update:settings="updateSettings"
                        @save="handleSave"
                    ></email-ai-settings-panel>
                </div>
            </div>
        </div>

        <sidebar-with-slots
            v-if="imageEditorVisible && editingImageBlockIndex !== null"
            max-width-class="max-w-[420px]"
            @close="closeImageEditor"
        >
            <template #title>
                <h1>{{ _mft('blockEditor:block.image.name') }}</h1>
            </template>

            <template #body>
                <div class="-m-6 p-6">
                    <blocks-email-image-with-link
                        :model-value="editableBlocks[editingImageBlockIndex]?.content || {}"
                        @update:modelValue="updateImageBlockContent"
                    ></blocks-email-image-with-link>
                </div>
            </template>
        </sidebar-with-slots>
    </div>
</template>
